<template>
  <PaymentBox>
    <BoxContent
      :title="'Pagamento Pix'"
      :description="'Copie o código abaixo e utilize o Pix Copia e Cola no aplicativo que você vai fazer o pagamento:'"
      :icon="'PixIcon'"
      :animated="false"
    >
      <div class="payment__cp-area" @click="copyText(pixCode)">
        <span class="payment__cp-area-text">{{ pixCode }}</span>
        <CopyPasteSvg />
      </div>
      <VButton text="Copiar código" @click.native="copyText(pixCode)" />
      <div :class="`payment__timer ${percentualTime <= 20 && `payment__timer--end`}`">
        <span class="payment__timer-text">O tempo para pagar termina em:</span>
        <strong class="payment__timer-time">{{ normalizedTimer }}</strong>
        <div class="payment__progress-bar" :style="`width: ${percentualTime}%`"></div>
      </div>
      <VButton text="Ver instruções" @click.native="toggleDrawer" variant="tertiary" />
    </BoxContent>
    <DrawerBottom
      :open="openDrawer"
      @toggle="openDrawer = !openDrawer"
      @close="openDrawer = false"
      v-if="openDrawer"
    >
      <template #content>
        <div class="drawer-content">
          <h3 class="drawer__title">Pix Copia e Cola</h3>

          <ul class="drawer__list">
            <li>
              <strong>1º passo</strong>
              <span>Copie o código que foi gerado</span>
            </li>
            <li>
              <strong>2º passo</strong>
              <span
                >Abra um aplicativo em que você tenha o Pix habilitado e use a opção Pix Copia e
                Cola</span
              >
            </li>
            <li>
              <strong>3º passo</strong>
              <span
                >Cole o código, confirme o valor e faça o pagamento. Ele será confirmado na hora
                :)</span
              >
            </li>
          </ul>

          <VButton text="Entendi" @click.native="toggleDrawer" />
        </div>
      </template>
    </DrawerBottom>
  </PaymentBox>
</template>

<script>
import PaymentBox from '@/components/layout/PaymentBox';
import BoxContent from '@/components/payments/BoxContent';
import DrawerBottom from '@/components/drawerBottom.component.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { dataLayersMixin } from '@/utils/mixins';

export default {
  name: 'PaymentPix',
  components: {
    PaymentBox,
    BoxContent,
    DrawerBottom,
  },
  mixins: [dataLayersMixin],
  data() {
    return {
      countdownTimer: 0,
      syncPayment: null,
      hasCountdown: true,
      originalCountdownTime: 300,
      countdownTotalTime: 300, // in seconds
      openDrawer: false,
      paymentId: null,
      paymentStatus: 0,
      unwatchCounter: null,
      pixCode: '',
      pixQrCode: '',
    };
  },
  methods: {
    ...mapActions({
      generatePixPayment: 'generatePixPayment',
      getPixPaymentStatus: 'getPixPaymentStatus',
    }),
    ...mapGetters({
      getPixCode: 'getPixCode',
    }),
    ...mapMutations(['enableLoader']),
    setUpCountdown() {
      this.countdownTimer = this.countdownTotalTime;

      setInterval(async () => {
        this.countdownTimer--;

        if (this.countdownTimer < 0) {
          this.countdownTimer = this.countdownTotalTime;
          this.hasCountdown = false;
        }
      }, 1000);
    },
    secondsToTime(e) {
      const m = Math.floor((e % 3600) / 60)
        .toString()
        .padStart(2, '0');
      const s = Math.floor(e % 60)
        .toString()
        .padStart(2, '0');

      return `${m}:${s}`;
    },
    async copyText(data) {
      try {
        await navigator.clipboard.writeText(data);
        this.$toasted.success('Código copiado com sucesso', {
          position: 'top-right',
          duration: 4000,
        });
      } catch {
        this.$toasted.error('Erro ao copiar código Pix. Tente novamente.', {
          position: 'top-right',
          duration: 4000,
        });
        console.error('Error to copy Pix Text');
      }
    },
    toggleDrawer() {
      this.openDrawer = !this.openDrawer;
    },
    goToPaymentSuccess() {
      this.$router.push({ name: 'pix-payment-confirmed' });
    },
    goToPaymentError() {
      this.$router.push({ name: 'pix-payment-error' });
    },
    goToGeneratePixError() {
      this.$router.push({ name: 'pix-payment-error-code' });
    },
  },
  computed: {
    normalizedTimer() {
      return this.secondsToTime(this.countdownTimer);
    },
    percentualTime() {
      return (this.countdownTimer / this.originalCountdownTime) * 100;
    },
  },
  created() {
    this.setUpCountdown();

    this.mostCommonPagesAnalytics({
      eventType: 'funnel_step',
      currentStep: 'Pagamento com pix',
    });

    this.unwatchCounter = this.$watch('hasCountdown', () => {
      if (!this.hasCountdown) {
        clearInterval(this.syncPayment);
        this.goToPaymentError();
      }
    });
  },
  async mounted() {
    this.enableLoader(true);

    try {
      const response = await this.generatePixPayment();

      if (!response) {
        return this.goToGeneratePixError();
      }

      this.pixCode = response.code;

      this.syncPayment = setInterval(async () => {
        const responseSale = await this.getPixPaymentStatus();
        this.paymentStatus = responseSale.paymentStatus;

        if (this.paymentStatus === 2) {
          clearInterval(this.syncPayment);
          this.unwatchCounter();

          this.goToPaymentSuccess();
        }
      }, 3000);
    } catch (error) {
      console.log(error);
      return this.goToGeneratePixError();
    } finally {
      this.enableLoader(false);
    }
  },
  beforeDestroy() {
    clearInterval(this.syncPayment);
  },
};
</script>

<style lang="scss" scoped>
.payment__cp-area {
  background: #f6f6f6;
  border-radius: 8px;
  padding: 10px;
  margin: 16px 0;
  font-weight: 300;
  font-size: 14px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  &-text {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
  }
}

.payment__timer-text {
  font-weight: 300;
  font-size: 14px;
  line-height: 170%;
  display: block;
  text-align: left;
}

.payment__timer-time {
  font-weight: 700;
  font-size: 20px;
  line-height: 120%;
  display: block;
  text-align: left;
}

.payment__progress-bar {
  background: #0234ec;
  border-radius: 0px 90px 90px 0px;
  width: 100%;
  height: 4px;
  margin-top: 8px;
}

.payment__timer {
  width: 100%;
  margin: 24px 0;

  &--end {
    & .payment__timer-time {
      color: var(--color-error);
    }

    .payment__progress-bar {
      background: var(--color-error);
    }
  }
}

.drawer__title {
  font-weight: 700;
  font-size: 20px;
  margin-bottom: 24px;
}

.drawer__list {
  color: #797c86;
  font-size: 16px;

  strong {
    font-weight: 700;
    display: block;
  }

  span {
    font-weight: 300;
  }

  li {
    display: block;
    // &:not:last-child() {
    margin-bottom: 24px;
    // }
  }
}
</style>
